import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-laft-legal-investigation-form',
  templateUrl: './laft-legal-investigation-form.component.html',
  styleUrls: ['./laft-legal-investigation-form.component.scss']
})
export class LaftLegalInvestigationFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;
  @Input() isClient = false;
  @Input() isSaving = false;

  public investigationForm: FormGroup;
  private formSubmitSubject$ = new Subject<void>();

  public caseStatusOptions = [
    { value: 'Investigación en fiscalía', text: 'Investigación en fiscalía' },
    { value: 'Acusación juicio oral o condena', text: 'Acusación juicio oral o condena' }
  ];

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.investigationForm = this.formBuilder.group({
      isInvestigated: [false, [Validators.required]],
      investigations: this.formBuilder.array([]),
      acceptsContract: [false, this.isClient ? [] : [Validators.requiredTrue]],
      acceptsCommercialUse: [false]
    });

    this.investigationForm
      .get('isInvestigated')
      ?.valueChanges.subscribe((isInvestigated: boolean) => this.handleInvestigationToggle(isInvestigated));

    this.investigationForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const cleanedData = this.getCleanedData();
      this.formChangesEvent.emit(cleanedData);
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.investigationForm.markAsDirty();
          this.investigationForm.markAllAsTouched();
          this.markInvestigationsAsTouched();
          Object.values(this.investigationForm.controls).forEach(control => {
            control.updateValueAndValidity({ emitEvent: true });
          });
        }),
        filter(() => this.investigationForm.valid)
      )
      .subscribe(() => {
        const cleanedData = this.getCleanedData();
        this.nextStep.next(cleanedData);
      });

    if (this.initData) {
      this.populateFormData();
    }
  }

  get investigations(): FormArray {
    return this.investigationForm.get('investigations') as FormArray;
  }

  onAddInvestigation(): void {
    this.investigations.push(this.createInvestigationGroup());
    this.setInvestigationValidators(this.investigationForm.get('isInvestigated')?.value);
  }

  onContinue(): void {
    if (this.isSaving) {
      return;
    }

    this.formSubmitSubject$.next();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private createInvestigationGroup(data?: any): FormGroup {
    return this.formBuilder.group({
      prosecutorOffice: [data?.prosecutorOffice || '', []],
      caseStatus: [data?.caseStatus || '', []],
      caseNumber: [data?.caseNumber || '', []],
      defendantName: [data?.defendantName || '', []]
    });
  }

  private handleInvestigationToggle(isInvestigated: boolean): void {
    if (!isInvestigated) {
      while (this.investigations.length) {
        this.investigations.removeAt(0);
      }
    } else if (isInvestigated && this.investigations.length === 0) {
      this.onAddInvestigation();
    }

    this.setInvestigationValidators(isInvestigated);
    this.updateInvestigationsArrayValidator(isInvestigated);
  }

  private setInvestigationValidators(isInvestigated: boolean): void {
    (this.investigations.controls as FormGroup[]).forEach(group => {
      Object.keys(group.controls).forEach(controlName => {
        const control = group.get(controlName);

        if (!control) {
          return;
        }

        if (isInvestigated) {
          control.setValidators([Validators.required]);
        } else {
          control.clearValidators();
          control.setValue('');
        }

        control.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  private updateInvestigationsArrayValidator(isInvestigated: boolean): void {
    const investigationsArray = this.investigations;

    if (isInvestigated) {
      investigationsArray.setValidators([Validators.required, this.atLeastOneInvestigation]);
      investigationsArray.updateValueAndValidity({ emitEvent: false });
    } else {
      investigationsArray.clearValidators();
      investigationsArray.setErrors(null);
      investigationsArray.updateValueAndValidity({ emitEvent: false });
    }
  }

  private populateFormData(): void {
    const investigations = this.initData?.investigations || [];

    if (investigations.length) {
      investigations.forEach((investigation: any) => {
        this.investigations.push(this.createInvestigationGroup(investigation));
      });
    }

    // Set checkbox values explicitly with emitEvent: false to prevent triggering subscriptions
    const acceptsContract = this.initData?.acceptsContract ?? false;
    const acceptsCommercialUse = this.initData?.acceptsCommercialUse ?? false;
    const isInvestigated = this.initData?.isInvestigated ?? false;

    if (!this.isClient) {
      this.investigationForm.get('acceptsContract')?.setValue(acceptsContract, { emitEvent: false });
    }
    this.investigationForm.get('acceptsCommercialUse')?.setValue(acceptsCommercialUse, { emitEvent: false });
    this.investigationForm.get('isInvestigated')?.setValue(isInvestigated, { emitEvent: false });

    if (!investigations.length && isInvestigated) {
      this.onAddInvestigation();
    }

    this.handleInvestigationToggle(isInvestigated);
  }

  private getCleanedData(): any {
    const data = { ...this.investigationForm.value };

    const rawInvestigations = data.investigations;
    const investigations = Array.isArray(rawInvestigations)
      ? rawInvestigations.filter(inv => Object.values(inv || {}).some(value => value))
      : [];

    if (data.isInvestigated) {
      data.investigations = Array.isArray(investigations) ? investigations : [];
    } else {
      delete data.investigations;
    }

    if (this.isClient) {
      delete data.acceptsContract;
    }

    return data;
  }

  private atLeastOneInvestigation(control: AbstractControl): ValidationErrors | null {
    const array = control as FormArray;
    return array.length > 0 ? null : { required: true };
  }

  private markInvestigationsAsTouched(): void {
    if (!this.investigationForm.get('isInvestigated')?.value) {
      return;
    }

    this.investigations.markAllAsTouched();
    (this.investigations.controls as FormGroup[]).forEach(group => {
      Object.keys(group.controls).forEach(controlName => {
        const control = group.get(controlName);
        control?.markAsTouched();
        control?.markAsDirty();
        control?.updateValueAndValidity({ emitEvent: true });
      });
    });
    this.investigations.updateValueAndValidity({ emitEvent: true });
  }
}
